import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Card, List, Tag, Button, Space, Typography, Collapse, Progress, Tooltip, Empty, Badge, Alert, message, Modal, Table, Spin, Divider } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    TableOutlined,
    FilterOutlined,
    BranchesOutlined,
    PlayCircleOutlined,
    ClockCircleOutlined,
    DatabaseOutlined,
    CodeOutlined,
    FileTextOutlined,
    MergeCellsOutlined,
    ExportOutlined,
    BugOutlined,
    AimOutlined,
    EyeOutlined,
    BlockOutlined,
    CheckSquareOutlined
} from '@ant-design/icons';
import WorkflowAPIService from '../../services/WorkflowAPIService';

const { Text, Title } = Typography;
const { Panel } = Collapse;

// Map node types to icons
const NODE_TYPE_ICONS = {
    'excel_input': <FileExcelOutlined/>,
    'filter': <FilterOutlined />,
    'if_else': <BranchesOutlined />,
    'transform': <CodeOutlined />,
    'merge': <MergeCellsOutlined />,
    'set_dataset_name': <DatabaseOutlined />,
    'export_excel': <ExportOutlined />,
    'output': <FileTextOutlined />,
    'batch_start': <BlockOutlined />,
    'batch_end': <CheckSquareOutlined />,
};

// Map node types to labels
const NODE_TYPE_LABELS = {
    'excel_input': 'Excel Input',
    'filter': 'Filter',
    'if_else': 'If/Else',
    'transform': 'Transform',
    'merge': 'Merge',
    'set_dataset_name': 'Set Dataset Name',
    'export_excel': 'Export Excel',
    'output': 'Output',
    'batch_start': 'Batch Start',
    'batch_end': 'Batch End',
};

const ExecutionResultsPanel = ({ executionResult, nodes = [], onNodeSelect, selectedNodeId }) => {
    // Ref for scrolling to selected item
    const listRef = useRef(null);
    const itemRefs = useRef({});

    // CSV Preview Modal state
    const [csvPreviewModal, setCsvPreviewModal] = useState({
        visible: false,
        loading: false,
        data: [],
        columns: [],
        nodeName: '',
        filePath: '',
        totalRows: 0
    });

    // Debug log
    useEffect(() => {
        console.log('ExecutionResultsPanel selectedNodeId:', selectedNodeId);
    }, [selectedNodeId]);

    // Auto scroll to selected node when it changes
    useEffect(() => {
        if (selectedNodeId && itemRefs.current[selectedNodeId]) {
            itemRefs.current[selectedNodeId].scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [selectedNodeId]);

    // Process execution results to create a clear list
    const processedResults = useMemo(() => {
        if (!executionResult || !executionResult.results) {
            return [];
        }

        const results = executionResult.results;
        const nodeMap = {};

        // Create a map of nodes by ID
        nodes.forEach(node => {
            nodeMap[node.id] = node;
        });

        // Process results
        return Object.entries(results).map(([nodeId, result]) => {
            const node = nodeMap[nodeId];
            const nodeType = node?.data?.nodeType || node?.data?.type || 'unknown';
            const nodeName = node?.data?.config?.name || node?.data?.label || nodeId;

            // Determine result type and extract relevant info
            let resultType = 'unknown';
            let resultInfo = {};
            let hasDownload = false;
            let hasCsvDownload = false;
            let csvFilePath = null;
            let downloadId = null;

            if (result) {
                if (result.filePath) {
                    // File output (Excel export, CSV, etc.)
                    resultType = 'file';
                    hasDownload = true;
                    // Generate a download ID instead of exposing file path
                    downloadId = btoa(result.filePath);

                    // Check if it's a CSV file
                    if (result.filePath.endsWith('.csv')) {
                        hasCsvDownload = true;
                        csvFilePath = result.filePath;
                    }

                    resultInfo = {
                        fileName: result.fileName || 'output.xlsx',
                        sheetCount: result.sheetCount,
                        sheets: result.sheets
                    };
                } else if (typeof result === 'string' && result.endsWith('.csv')) {
                    // Direct CSV file path
                    resultType = 'csv_file';
                    hasCsvDownload = true;
                    csvFilePath = result;
                    resultInfo = {
                        fileName: result.split('/').pop() || 'data.csv'
                    };
                } else if (result.type === 'dataset' || result.rowCount !== undefined) {
                    // Dataset result
                    resultType = 'dataset';
                    // Check if dataset has CSV file path
                    if (result.csvPath || result.filePath) {
                        hasCsvDownload = true;
                        csvFilePath = result.csvPath || result.filePath;
                    }
                    resultInfo = {
                        rowCount: result.rowCount || 0,
                        headers: result.headers || [],
                        columnCount: result.headers?.length || 0
                    };
                } else if (result._datasetName) {
                    // Named dataset
                    resultType = 'named_dataset';
                    resultInfo = {
                        datasetName: result._datasetName,
                        rowCount: result.data?.length || 0
                    };
                } else if (Array.isArray(result)) {
                    // Array data
                    resultType = 'array';
                    resultInfo = {
                        count: result.length
                    };
                } else if (result._list) {
                    // List of datasets (from merge)
                    resultType = 'merged_list';
                    resultInfo = {
                        datasetCount: result._list.length
                    };
                } else if (result._isBatchNode && result._iterations) {
                    // Batch processing result with multiple iterations
                    // Each iteration now contains only THIS node's result (not all nodes)
                    console.log('[ExecutionResultsPanel] Batch node result for', nodeId, ':', result);
                    resultType = 'batch_result';
                    const iterationNames = Object.keys(result._iterations);
                    console.log('[ExecutionResultsPanel] Iteration names:', iterationNames);
                    console.log('[ExecutionResultsPanel] First iteration data:', result._iterations[iterationNames[0]]);
                    
                    // Store iteration data - each iteration has this node's result directly
                    const iterations = iterationNames.map(name => {
                        const iterResult = result._iterations[name];
                        
                        // Check if result has CSV file for preview
                        let hasCsvPreview = false;
                        let csvPath = null;
                        
                        if (typeof iterResult === 'string' && iterResult.endsWith('.csv')) {
                            hasCsvPreview = true;
                            csvPath = iterResult;
                        } else if (iterResult?.csvPath || iterResult?.filePath) {
                            hasCsvPreview = true;
                            csvPath = iterResult.csvPath || iterResult.filePath;
                        }
                        
                        return {
                            name,
                            result: iterResult,
                            hasCsvPreview,
                            csvPath
                        };
                    });
                    
                    resultInfo = {
                        iterationCount: iterationNames.length,
                        iterationNames: iterationNames,
                        iterations: iterations // Data for nested display
                    };
                } else if (result._isBatchStart && result._iterations) {
                    // BatchStart output
                    resultType = 'batch_start';
                    resultInfo = {
                        iterationCount: result._iterations.length,
                        mode: result._mode || 'sequential',
                        datasetNames: result._iterations.map(it => it.name)
                    };
                } else if (typeof result === 'object') {
                    // Check for CSV path in object
                    if (result.csvPath) {
                        hasCsvDownload = true;
                        csvFilePath = result.csvPath;
                    }
                    resultType = 'object';
                    resultInfo = {
                        keys: Object.keys(result).slice(0, 5)
                    };
                }
            }

            return {
                nodeId,
                nodeName,
                nodeType,
                success: result?.success !== false,
                error: result?.error,
                resultType,
                resultInfo,
                hasDownload,
                hasCsvDownload,
                csvFilePath,
                downloadId,
                rawResult: result
            };
        });
    }, [executionResult, nodes]);

    // Calculate summary stats
    const stats = useMemo(() => {
        const total = processedResults.length;
        const successful = processedResults.filter(r => r.success).length;
        const failed = total - successful;
        const hasDownloads = processedResults.filter(r => r.hasDownload).length;
        const hasCsvDownloads = processedResults.filter(r => r.hasCsvDownload).length;

        return { total, successful, failed, hasDownloads, hasCsvDownloads };
    }, [processedResults]);

    // Handle download using API service
    const handleDownload = async (nodeId, fileName) => {
        try {
            const result = executionResult.results[nodeId];
            if (result && result.filePath) {
                // Use the API service to download - never expose actual file path to user
                await WorkflowAPIService.downloadFile(result.filePath, fileName || 'download.xlsx');
            }
        } catch (error) {
            console.error('Download error:', error);
            message.error('Failed to download file');
        }
    };

    // Handle CSV preview - show modal with sample data
    const handleCsvPreview = async (item) => {
        let filePath = item.csvFilePath;

        // If no direct CSV path, check rawResult
        if (!filePath && item.rawResult) {
            if (typeof item.rawResult === 'string' && item.rawResult.endsWith('.csv')) {
                filePath = item.rawResult;
            } else if (item.rawResult.filePath && item.rawResult.filePath.endsWith('.csv')) {
                filePath = item.rawResult.filePath;
            }
        }

        if (!filePath) {
            message.warning('No CSV file available for this node');
            return;
        }

        setCsvPreviewModal({
            visible: true,
            loading: true,
            data: [],
            columns: [],
            nodeName: item.nodeName,
            filePath: filePath,
            totalRows: 0
        });

        try {
            // Fetch CSV preview data from API
            const response = await WorkflowAPIService.previewCsv(filePath, 200);

            if (response.success && response.data) {
                const { headers, rows, totalRows } = response.data;

                // Build table columns
                const columns = headers.map((header, idx) => ({
                    title: header,
                    dataIndex: header,
                    key: header,
                    ellipsis: true,
                    width: 150
                }));

                // Build table data with key
                const data = rows.map((row, idx) => ({
                    key: idx,
                    ...row
                }));

                setCsvPreviewModal(prev => ({
                    ...prev,
                    loading: false,
                    data,
                    columns,
                    totalRows: totalRows || rows.length
                }));
            } else {
                throw new Error(response.message || 'Failed to load CSV preview');
            }
        } catch (error) {
            console.error('CSV Preview error:', error);
            message.error('Failed to load CSV preview: ' + error.message);
            setCsvPreviewModal(prev => ({
                ...prev,
                loading: false,
                visible: false
            }));
        }
    };

    // Handle CSV download for node results
    const handleCsvDownload = async (item) => {
        try {
            let filePath = item?.csvFilePath || csvPreviewModal.filePath;

            // If no direct CSV path, check rawResult
            if (!filePath && item?.rawResult) {
                if (typeof item.rawResult === 'string' && item.rawResult.endsWith('.csv')) {
                    filePath = item.rawResult;
                } else if (item.rawResult.filePath && item.rawResult.filePath.endsWith('.csv')) {
                    filePath = item.rawResult.filePath;
                }
            }

            if (filePath) {
                const nodeName = item?.nodeName || csvPreviewModal.nodeName;
                const fileName = `${nodeName.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.csv`;
                await WorkflowAPIService.downloadFile(filePath, filePath.split('/').pop() || fileName);
                message.success(`Downloaded: ${nodeName}`);
            } else {
                message.warning('No CSV file available for this node');
            }
        } catch (error) {
            console.error('CSV Download error:', error);
            message.error('Failed to download CSV');
        }
    };

    // Render result info based on type
    // Helper function to render a single node result (reusable for batch iterations)
    const renderNodeResult = (nodeId, result, nodeMap) => {
        const node = nodeMap[nodeId];
        const nodeType = node?.data?.nodeType || node?.data?.type || 'unknown';
        const nodeName = node?.data?.config?.name || node?.data?.label || nodeId;
        
        // Determine result display based on type
        let content = null;
        let hasCsvPreview = false;
        let csvPath = null;

        if (typeof result === 'string' && result.endsWith('.csv')) {
            // Direct CSV path
            hasCsvPreview = true;
            csvPath = result;
            content = (
                <Text type="secondary">
                    <TableOutlined style={{ marginRight: 4 }} />
                    CSV file: {result.split('/').pop()}
                </Text>
            );
        } else if (result?.csvPath || result?.filePath) {
            // Result with CSV path
            hasCsvPreview = true;
            csvPath = result.csvPath || result.filePath;
            const rowCount = result.rowCount || result.data?.length || '?';
            content = (
                <Text type="secondary">
                    <TableOutlined style={{ marginRight: 4 }} />
                    {rowCount} rows
                </Text>
            );
        } else if (result?.rowCount !== undefined) {
            // Dataset result
            content = (
                <Text type="secondary">
                    <TableOutlined style={{ marginRight: 4 }} />
                    {result.rowCount} rows × {result.headers?.length || '?'} columns
                </Text>
            );
        } else if (result?._datasetName) {
            // Named dataset
            content = (
                <Text type="secondary">
                    <DatabaseOutlined style={{ marginRight: 4 }} />
                    "{result._datasetName}" - {result.data?.length || 0} rows
                </Text>
            );
        } else if (Array.isArray(result)) {
            // Array
            content = (
                <Text type="secondary">
                    <DatabaseOutlined style={{ marginRight: 4 }} />
                    Array with {result.length} items
                </Text>
            );
        } else if (typeof result === 'object' && result !== null) {
            // Generic object
            const keys = Object.keys(result).slice(0, 3);
            content = (
                <Text type="secondary">
                    <CodeOutlined style={{ marginRight: 4 }} />
                    Object: {keys.join(', ')}{keys.length >= 3 ? '...' : ''}
                </Text>
            );
        } else {
            // Primitive value
            content = (
                <Text type="secondary">
                    Value: {String(result)}
                </Text>
            );
        }

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 12px',
                background: '#fafafa',
                borderRadius: 4,
                marginBottom: 6
            }}>
                <Space>
                    <Tag color="blue" style={{ margin: 0 }}>
                        {NODE_TYPE_ICONS[nodeType] || <CodeOutlined />}
                        {' '}
                        {NODE_TYPE_LABELS[nodeType] || nodeType}
                    </Tag>
                    <Text strong style={{ minWidth: 100 }}>{nodeName}</Text>
                    {content}
                </Space>
                {hasCsvPreview && (
                    <Button
                        size="small"
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCsvPreview({
                                nodeName,
                                csvFilePath: csvPath
                            });
                        }}
                    >
                        View
                    </Button>
                )}
            </div>
        );
    };

    const renderResultInfo = (item) => {
        switch (item.resultType) {
            case 'file':
                return (
                    <div>
                        <Space direction="vertical" size={2}>
                            <Text><FileExcelOutlined /> {item.resultInfo.fileName}</Text>
                            {item.resultInfo.sheetCount && (
                                <Text type="secondary">{item.resultInfo.sheetCount} sheet(s)</Text>
                            )}
                            {item.resultInfo.sheets && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Sheets: {item.resultInfo.sheets.join(', ')}
                                </Text>
                            )}
                        </Space>
                    </div>
                );

            case 'csv_file':
                return (
                    <Space>
                        <Tooltip title="Click to download">
                            <Text
                                type="secondary"
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCsvDownload(item);
                                }}
                            >
                                <TableOutlined style={{ marginRight: 3 }} /> {item.resultInfo.fileName} <DownloadOutlined style={{ fontSize: 12 }} />
                            </Text>
                        </Tooltip>
                    </Space>
                );

            case 'dataset':
                return (
                    <Space>
                        <Text type="secondary"><TableOutlined /> {item.resultInfo.rowCount} rows × {item.resultInfo.columnCount} columns</Text>
                    </Space>
                );

            case 'named_dataset':
                return (
                    <Space>
                        <Text type="secondary"><DatabaseOutlined /> "{item.resultInfo.datasetName}" - {item.resultInfo.rowCount} rows</Text>
                    </Space>
                );

            case 'merged_list':
                return (
                    <Space>
                        <Text type="secondary"><MergeCellsOutlined /> {item.resultInfo.datasetCount} datasets merged</Text>
                    </Space>
                );

            case 'array':
                return (
                    <Space>
                        <Text type="secondary"><DatabaseOutlined /> {item.resultInfo.count} items</Text>
                    </Space>
                );

            case 'object':
                return (
                    <Text type="secondary">
                        Object with keys: {item.resultInfo.keys.join(', ')}
                        {item.resultInfo.keys.length >= 5 ? '...' : ''}
                    </Text>
                );

            case 'batch_result':
                // Create nodeMap for helper function
                const nodeMap = {};
                nodes.forEach(node => {
                    nodeMap[node.id] = node;
                });

                return (
                    <div style={{ width: '100%' }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary">
                                <BlockOutlined style={{ marginRight: 4 }} />
                                {item.resultInfo.iterationCount} iterations
                            </Text>
                        </div>
                        
                        {/* Nested Collapse for each iteration */}
                        <Collapse
                            size="small"
                            ghost
                            expandIconPosition="start"
                            style={{ 
                                background: '#fff',
                                border: '1px solid #e8e8e8',
                                borderRadius: 4
                            }}
                        >
                            {item.resultInfo.iterations?.map((iteration, idx) => {
                                const result = iteration.result;
                                const hasResult = result != null;
                                
                                // Determine result type and display info
                                let displayContent = null;
                                let rowCount = null;
                                
                                if (typeof result === 'string' && result.endsWith('.csv')) {
                                    displayContent = <Text type="secondary"><TableOutlined /> CSV: {result.split('/').pop()}</Text>;
                                } else if (result?.rowCount !== undefined) {
                                    rowCount = result.rowCount;
                                    const colCount = result.headers?.length || result.columnCount || '?';
                                    displayContent = <Text type="secondary"><TableOutlined /> {rowCount} rows × {colCount} columns</Text>;
                                } else if (result?.csvPath || result?.filePath) {
                                    rowCount = result.rowCount || result.data?.length;
                                    displayContent = <Text type="secondary"><TableOutlined /> {rowCount ? `${rowCount} rows` : 'CSV file'}</Text>;
                                } else if (result?._datasetName) {
                                    displayContent = <Text type="secondary"><DatabaseOutlined /> "{result._datasetName}"</Text>;
                                } else if (Array.isArray(result)) {
                                    displayContent = <Text type="secondary"><DatabaseOutlined /> {result.length} items</Text>;
                                } else if (typeof result === 'object') {
                                    displayContent = <Text type="secondary"><CodeOutlined /> Object</Text>;
                                }

                                return (
                                    <Panel
                                        key={idx}
                                        header={
                                            <Space>
                                                <Tag color="cyan">#{idx + 1}</Tag>
                                                <Text strong>{iteration.name}</Text>
                                                {displayContent}
                                            </Space>
                                        }
                                        style={{
                                            marginBottom: idx < item.resultInfo.iterations.length - 1 ? 4 : 0,
                                            borderBottom: idx < item.resultInfo.iterations.length - 1 ? '1px solid #f0f0f0' : 'none'
                                        }}
                                    >
                                        {hasResult ? (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '8px 12px',
                                                background: '#fafafa',
                                                borderRadius: 4
                                            }}>
                                                <Space>
                                                    <Text strong style={{ color: '#1890ff' }}>{item.nodeName}</Text>
                                                    {displayContent}
                                                </Space>
                                                {iteration.hasCsvPreview && (
                                                    <Space>
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            icon={<EyeOutlined />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCsvPreview({
                                                                    nodeName: `${item.nodeName} - ${iteration.name}`,
                                                                    csvFilePath: iteration.csvPath
                                                                });
                                                            }}
                                                        >
                                                            View
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            icon={<DownloadOutlined />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCsvDownload({
                                                                    nodeName: `${item.nodeName}_${iteration.name}`,
                                                                    csvFilePath: iteration.csvPath
                                                                });
                                                            }}
                                                        >
                                                            Download
                                                        </Button>
                                                    </Space>
                                                )}
                                            </div>
                                        ) : (
                                            <Text type="secondary" style={{ fontStyle: 'italic' }}>
                                                No result for this iteration
                                            </Text>
                                        )}
                                    </Panel>
                                );
                            })}
                        </Collapse>
                    </div>
                );

            case 'batch_start':
                return (
                    <Space>
                        <Text type="secondary">
                            <BlockOutlined style={{ marginRight: 4 }} />
                            {item.resultInfo.iterationCount} datasets ({item.resultInfo.mode})
                        </Text>
                    </Space>
                );

            default:
                return <Text type="secondary">Processed</Text>;
        }
    };

    if (!executionResult) {
        return (
            <Empty
                description="No execution results yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
        );
    }

    return (
        <div style={{ padding: 16 }}>
            {/* Error Banner - show when workflow failed */}
            {!executionResult.success && executionResult.error && (
                <Alert
                    type="error"
                    icon={<CloseCircleOutlined />}
                    message={
                        <Space>
                            <Text strong>Execution Failed</Text>
                        </Space>
                    }
                    description={
                        <Text type="danger">
                            {executionResult.error}
                        </Text>
                    }
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}

            {/* Debug Mode Banner */}
            {executionResult.isDebugResult && (
                <Alert
                    type="warning"
                    icon={<BugOutlined />}
                    message={
                        <Space>
                            <Text strong>Debug Mode</Text>
                            <Tag color="orange" icon={<AimOutlined />}>
                                Stopped at: {executionResult.debugNodeName || 'Unknown'}
                            </Tag>
                        </Space>
                    }
                    description={
                        <Text type="secondary">
                            Execution stopped at the debug breakpoint. Only nodes up to and including "{executionResult.debugNodeName}" were executed.
                        </Text>
                    }
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}

            {/* Summary Header */}
            <Card
                size="small"
                style={{ marginBottom: 16 }}
                bodyStyle={{ padding: '12px 16px' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space size="large">
                        {/* Status */}
                        <div style={{ textAlign: 'center' }}>
                            {executionResult.success ? (
                                <Badge status="success" text={<Text strong style={{ color: '#52c41a' }}>Success</Text>} />
                            ) : (
                                <Badge status="error" text={<Text strong style={{ color: '#ff4d4f' }}>Failed</Text>} />
                            )}
                        </div>

                        {/* Time */}
                        <div>
                            <ClockCircleOutlined style={{ marginRight: 6 }} />
                            <Text>{executionResult.executionTime || 0}ms</Text>
                        </div>

                        {/* Node count */}
                        <div>
                            <Text type="secondary">Nodes: </Text>
                            <Text strong style={{ color: '#52c41a' }}>{stats.successful}</Text>
                            {stats.failed > 0 && (
                                <>
                                    <Text type="secondary"> / </Text>
                                    <Text strong style={{ color: '#ff4d4f' }}>{stats.failed} failed</Text>
                                </>
                            )}
                        </div>

                        {/* Downloads */}
                        {stats.hasDownloads > 0 && (
                            <div>
                                <DownloadOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                                <Text>{stats.hasDownloads} file(s)</Text>
                            </div>
                        )}
                    </Space>

                    {/* Progress */}
                    <Progress
                        type="circle"
                        percent={Math.round((stats.successful / stats.total) * 100)}
                        size={40}
                        strokeColor={stats.failed > 0 ? '#ff4d4f' : '#52c41a'}
                    />
                </div>
            </Card>

            {/* Downloadable Files Section */}
            {stats.hasDownloads > 0 && (
                <Card
                    size="small"
                    title={
                        <Space>
                            <DownloadOutlined style={{ color: '#1890ff' }} />
                            <Text strong>Download Files</Text>
                        </Space>
                    }
                    style={{ marginBottom: 16 }}
                    bodyStyle={{ padding: 12 }}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {processedResults
                            .filter(item => item.hasDownload)
                            .map(item => (
                                <div
                                    key={item.nodeId}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 12px',
                                        background: '#fafafa',
                                        borderRadius: 6,
                                        border: '1px solid #f0f0f0'
                                    }}
                                >
                                    <Space>
                                        <FileExcelOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                                        <div>
                                            <Text strong>{item.resultInfo.fileName}</Text>
                                            {item.resultInfo.sheetCount && (
                                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                                    ({item.resultInfo.sheetCount} sheets)
                                                </Text>
                                            )}
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    From: {item.nodeName}
                                                </Text>
                                            </div>
                                        </div>
                                    </Space>
                                    <Button
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownload(item.nodeId, item.resultInfo.fileName)}
                                    >
                                        Download
                                    </Button>
                                </div>
                            ))}
                    </Space>
                </Card>
            )}

            {/* Node Execution List */}
            <Card
                size="small"
                title={
                    <Space>
                        <PlayCircleOutlined />
                        <Text strong>Execution Details</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>(Click to select node)</Text>
                    </Space>
                }
                bodyStyle={{ padding: 0 }}
            >
                <List
                    ref={listRef}
                    size="small"
                    dataSource={processedResults}
                    renderItem={(item, index) => {
                        const isSelected = selectedNodeId === item.nodeId;
                        const getBackground = () => {
                            if (isSelected) return '#e6f4ff';
                            if (!item.success) return '#fff2f0';
                            return 'transparent';
                        };

                        return (
                            <List.Item
                                ref={el => itemRefs.current[item.nodeId] = el}
                                onClick={() => onNodeSelect && onNodeSelect(item.nodeId)}
                                style={{
                                    padding: '12px 16px',
                                    borderBottom: index < processedResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    background: getBackground(),
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected && item.success) e.currentTarget.style.background = '#f0f5ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = getBackground();
                                }}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <div style={{
                                            width: 32,
                                            height: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            background: isSelected ? '#e6f4ff' : (item.success ? '#f6ffed' : '#fff2f0'),
                                            border: isSelected
                                                ? '2px solid #1890ff'
                                                : `1px solid ${item.success ? '#b7eb8f' : '#ffa39e'}`,
                                            transition: 'all 0.2s'
                                        }}>
                                            {item.success ? (
                                                <CheckCircleOutlined style={{ color: isSelected ? '#1890ff' : '#52c41a' }} />
                                            ) : (
                                                <CloseCircleOutlined style={{ color: isSelected ? '#1890ff' : '#ff4d4f' }} />
                                            )}
                                        </div>
                                    }
                                    title={
                                        <div>
                                            <Text strong>{item.nodeName}</Text><br />
                                            <div>
                                                {item.error ? (
                                                    <Text type="danger">{item.error}</Text>
                                                ) : (
                                                    renderResultInfo(item)
                                                )}
                                            </div>
                                            <Divider dashed style={{ margin: '5px 0' }} />
                                            <div style={{ marginTop: 10 }}>
                                                <Tag color="blue">
                                                    {NODE_TYPE_ICONS[item.nodeType] || <CodeOutlined />}
                                                    {' '}
                                                    {NODE_TYPE_LABELS[item.nodeType] || item.nodeType}
                                                </Tag>
                                                {item.hasCsvDownload && !item.hasDownload && (
                                                    <Button
                                                        type='text'
                                                        size='small'
                                                        color="default"
                                                        variant='dashed'
                                                        icon={<EyeOutlined />}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCsvPreview(item);
                                                        }}
                                                    >
                                                        View
                                                    </Button>
                                                )}
                                                {item.hasDownload && (
                                                    <Tag color="green" icon={<DownloadOutlined />}>
                                                        File
                                                    </Tag>
                                                )}
                                            </div>
                                        </div>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Card>

            {/* Execution Log (Collapsible) */}
            {executionResult.executionLog && executionResult.executionLog.length > 0 && (
                <Collapse
                    ghost
                    style={{ marginTop: 16 }}
                    items={[{
                        key: 'log',
                        label: (
                            <Space>
                                <FileTextOutlined />
                                <Text>Execution Log ({executionResult.executionLog.length} entries)</Text>
                            </Space>
                        ),
                        children: (
                            <div style={{
                                maxHeight: 200,
                                overflowY: 'auto',
                                fontFamily: 'monospace',
                                fontSize: 11,
                                background: '#f5f5f5',
                                padding: 12,
                                borderRadius: 4
                            }}>
                                {executionResult.executionLog.map((entry, index) => (
                                    <div key={index} style={{ marginBottom: 4 }}>
                                        <Text type="secondary">[{entry.timestamp}]</Text>{' '}
                                        <Text style={{
                                            color: entry.level === 'error' ? '#ff4d4f' :
                                                entry.level === 'warning' ? '#faad14' : '#52c41a',
                                            fontWeight: 500
                                        }}>
                                            {entry.level?.toUpperCase()}
                                        </Text>
                                        : {entry.message}
                                    </div>
                                ))}
                            </div>
                        )
                    }]}
                />
            )}

            {/* CSV Preview Modal */}
            <Modal
                title={
                    <Space>
                        <TableOutlined style={{ color: '#13c2c2' }} />
                        <span>CSV Preview: {csvPreviewModal.nodeName}</span>
                        {csvPreviewModal.totalRows > 0 && (
                            <Tag color="blue">
                                {csvPreviewModal.data.length} / {csvPreviewModal.totalRows} rows
                            </Tag>
                        )}
                    </Space>
                }
                open={csvPreviewModal.visible}
                onCancel={() => setCsvPreviewModal(prev => ({ ...prev, visible: false }))}
                width={1000}
                footer={[
                    <Button
                        key="close"
                        onClick={() => setCsvPreviewModal(prev => ({ ...prev, visible: false }))}
                    >
                        Close
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleCsvDownload(null)}
                    >
                        Download CSV
                    </Button>
                ]}
                styles={{ body: { padding: '12px 24px', maxHeight: '60vh', overflow: 'auto' } }}
            >
                {csvPreviewModal.loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">Loading CSV data...</Text>
                        </div>
                    </div>
                ) : (
                    <>
                        {csvPreviewModal.data.length > 0 ? (
                            <Table
                                dataSource={csvPreviewModal.data}
                                columns={csvPreviewModal.columns}
                                size="small"
                                pagination={false}
                                scroll={{ x: 'max-content', y: 400 }}
                                bordered
                            />
                        ) : (
                            <Empty description="No data available" />
                        )}
                        {csvPreviewModal.totalRows > csvPreviewModal.data.length && (
                            <Alert
                                type="info"
                                message={`Showing first ${csvPreviewModal.data.length} rows of ${csvPreviewModal.totalRows} total rows`}
                                style={{ marginTop: 12 }}
                                showIcon
                            />
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
};

export default ExecutionResultsPanel;
